import i18next from "i18next";

import { LocalStorageKeys } from "@/common/constants";

import { HttpMethod, QueryParams, Type } from "@/Utils/request/types";

export const API = <TResponse, TBody = undefined>(
  route: `${HttpMethod} ${string}`,
) => {
  const [method, path] = route.split(" ") as [HttpMethod, string];
  return {
    path,
    method,
    TRes: Type<TResponse>(),
    TBody: Type<TBody>(),
  };
};

export function makeUrl(
  path: string,
  query?: QueryParams,
  pathParams?: Record<string, string | number>,
) {
  if (pathParams) {
    path = Object.entries(pathParams).reduce(
      (acc, [key, value]) => acc.replace(`{${key}}`, `${value}`),
      path,
    );
  }

  if (query) {
    const queryString = makeQueryParams(query);
    if (queryString) {
      path += `?${queryString}`;
    }
  }

  return path;
}

const makeQueryParams = (query: QueryParams) => {
  const qParams = new URLSearchParams();

  Object.entries(query).forEach(([key, value]) => {
    if (value === undefined) return;

    if (Array.isArray(value)) {
      value.forEach((v) => qParams.append(key, `${v}`));
      return;
    }

    qParams.set(key, `${value}`);
  });

  return qParams.toString();
};

export function makeHeaders(
  noAuth: boolean,
  additionalHeaders?: HeadersInit,
  isFormData?: boolean,
) {
  const headers = new Headers(additionalHeaders);

  // Don't set Content-Type for FormData - let browser set it with boundary
  if (!isFormData) {
    headers.set("Content-Type", "application/json");
  }
  headers.append("Accept", "application/json");

  const language = getAppLanguage();
  if (language && !headers.has("Accept-Language")) {
    headers.set("Accept-Language", language);
  }

  const authorizationHeader = getAuthorizationHeader();
  if (authorizationHeader && !noAuth) {
    headers.set("Authorization", authorizationHeader);
  }

  return headers;
}

export function getAppLanguage() {
  const language = i18next.resolvedLanguage || i18next.language;
  return language && language !== "cimode" ? language : null;
}

export function getAuthorizationHeader() {
  const accessToken = localStorage.getItem(LocalStorageKeys.accessToken);

  if (accessToken) {
    return `Bearer ${accessToken}`;
  }

  return null;
}

export async function getResponseBody<TData>(res: Response): Promise<TData> {
  if (!(res.headers.get("content-length") !== "0")) {
    return null as TData;
  }

  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const isBinary =
    contentType.includes("image") || contentType.includes("application/pdf");

  if (isBinary) {
    return (await res.blob()) as TData;
  }

  if (!isJson) {
    return (await res.text()) as TData;
  }

  try {
    return await res.json();
  } catch {
    return (await res.text()) as TData;
  }
}

export function swapElements<T>(arr: T[], idx1: number, idx2: number): T[] {
  if (idx1 < 0 || idx1 >= arr.length || idx2 < 0 || idx2 >= arr.length) {
    return arr;
  }
  const newArray = [...arr];
  [newArray[idx1], newArray[idx2]] = [newArray[idx2], newArray[idx1]];
  return newArray;
}
